import React, { useState } from 'react'
import { useBlocker } from '@tanstack/react-router'
import { Alert, Button, Flex, Tag, Tooltip, Typography } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { uploadLogo } from '../api'
import { useManagedForm, useUpdatePublicForm } from '../hooks'
import { draftAfterMutation } from '../draft-state'
import { configuration, Configuration, sameConfiguration } from '../public-form'
import { BrandingEditor } from './branding-editor'
import { FormSharing } from './form-sharing'
import { isAxiosError } from 'axios'
import { NetworkErrors } from '../../components/network-errors'

export function CampaignPublicForm({ campaignId }: { campaignId: string }) {
    const { t } = useTranslation()
    const query = useManagedForm(campaignId)
    const [localDraft, setLocalDraft] = useState<Configuration>()
    const draft = localDraft ?? query.data?.draft
    const [saved, setSaved] = useState(false)
    const dirty = !!draft && !!query.data && !sameConfiguration(draft, query.data.draft)
    useBlocker({
        shouldBlockFn: ({ current, next }) => {
            // Source tabs share this page and keep the draft mounted.
            if (current.pathname === next.pathname) return false
            return dirty && !window.confirm(t('You have unsaved changes. Are you sure you want to leave?'))
        },
        enableBeforeUnload: () => dirty,
    })
    const { mutation, contextHolder } = useUpdatePublicForm(campaignId, (_result, input) => {
        setLocalDraft((current) => draftAfterMutation(current, input))
        if (input.action !== 'unpublish') {
            setSaved(true)
        }
    })
    const error = mutation.error
        ? isAxiosError(mutation.error)
            ? Object.values(mutation.error.response?.data?.errors ?? {})
                  .flat()
                  .map((issue) => t(String(issue)))
                  .join(' ') || t('Could not save public form')
            : mutation.error.message
        : undefined
    if (query.isPending) return <NetworkErrors query={query} />
    if (query.isError)
        return (
            <Alert
                type="error"
                message={t('Could not load public form')}
                action={<Button onClick={() => void query.refetch()}>{t('Retry')}</Button>}
            />
        )
    if (!draft) return null
    const validDraft = configuration.safeParse(draft).success
    const changesToPublish = !query.data.enabled || !sameConfiguration(draft, query.data.published)
    return (
        <>
            {contextHolder}
            <Flex
                vertical
                gap="middle"
            >
                {!query.data.ready && (
                    <Alert
                        type="warning"
                        showIcon
                        message={query.data.publishingIssues.map((issue) => t(issue)).join(' ')}
                    />
                )}
                {error && (
                    <Alert
                        type="error"
                        showIcon
                        message={error}
                    />
                )}
                <Flex
                    gap="small"
                    wrap
                    align="center"
                    justify="space-between"
                    className="public-form-action-bar"
                >
                    <Typography.Text type={dirty ? 'warning' : saved ? 'success' : 'secondary'}>
                        {t(
                            dirty
                                ? 'Unsaved changes'
                                : query.data.enabled && !changesToPublish
                                  ? 'All changes published'
                                  : 'Draft saved'
                        )}
                    </Typography.Text>
                    <Flex
                        gap="small"
                        align="center"
                        wrap
                    >
                        <Flex
                            gap={0}
                            align="center"
                        >
                            <Button
                                loading={mutation.isPending}
                                onClick={() => mutation.mutate({ action: 'save', draft })}
                                disabled={!dirty || !validDraft}
                            >
                                {t('Save draft')}
                            </Button>
                            <Tooltip title={t('Saving a draft does not change the published form.')}>
                                <Button
                                    type="text"
                                    icon={<InfoCircleOutlined />}
                                    aria-label={t('Saving a draft does not change the published form.')}
                                />
                            </Tooltip>
                        </Flex>
                        <Button
                            type="primary"
                            disabled={!query.data.ready || !changesToPublish || !validDraft}
                            loading={mutation.isPending}
                            onClick={() => mutation.mutate({ action: 'publish', draft })}
                        >
                            {t(query.data.enabled ? 'Publish changes' : 'Publish form')}
                        </Button>
                    </Flex>
                </Flex>
                <BrandingEditor
                    value={draft}
                    onChange={(value) => {
                        setLocalDraft(value)
                        setSaved(false)
                    }}
                    publicationStatus={
                        <Tag color={query.data.enabled ? 'green' : 'default'}>
                            {t(query.data.enabled ? 'Published' : 'Unpublished')}
                        </Tag>
                    }
                    sharing={
                        <FormSharing
                            form={query.data}
                            unpublishedChanges={changesToPublish}
                            onUnpublish={() => mutation.mutate({ action: 'unpublish' })}
                            pending={mutation.isPending}
                        />
                    }
                    onUpload={(file) => uploadLogo(campaignId, file)}
                />
            </Flex>
        </>
    )
}
